import { IAnswer } from 'affiny-core/modules/api/models/reflist/reflist.types';
import {
  EReflistChapterName,
  IHydratedReflist,
  IReflistChapter,
  TReflistQuestion,
  TSearchReflistQuestion,
} from 'affiny-core/modules/api/repositories/reflists/reflists.types';
import { EReflistSubject } from 'affiny-core/modules/reflists/types';
import { IRootState } from 'affiny-core/modules/state';

/* ***** *****  Utils  ***** ***** */

export const selectChapterKeys = (state: IRootState) =>
  Object.keys(selectReflists(state)) as EReflistChapterName[];

export const selectChapterIndex = (
  state: IRootState,
  chapterLabel: EReflistChapterName
): number => {
  const chaptersKeys = selectChapterKeys(state);

  return chaptersKeys.indexOf(chapterLabel);
};

export const chapterExist = (
  state: IRootState,
  chapterLabel: EReflistChapterName
): boolean => {
  const chaptersKeys = selectChapterKeys(state);

  return chaptersKeys.includes(chapterLabel);
};

export const selectChapterNameByReflistKey = (
  state: IRootState,
  reflistKey: string,
  subject: EReflistSubject = EReflistSubject.ME
): EReflistChapterName | undefined => {
  const reflists = selectReflists(state, subject);
  let value: EReflistChapterName | undefined;

  Object.keys(reflists).map((chapterKey: EReflistChapterName) => {
    const chapter = reflists[chapterKey];
    const questions = chapter ? chapter.questions : [];

    return questions.filter((values: TReflistQuestion[]) =>
      values.filter((question: TReflistQuestion) => {
        if (question.label === reflistKey) {
          value = chapterKey as EReflistChapterName;

          return true;
        } else {
          return false;
        }
      })
    );
  });

  return value;
};

/* ***** *****  Selectors  ***** ***** */

export const selectReflists = (
  state: IRootState,
  subject: EReflistSubject = EReflistSubject.ME
): IHydratedReflist => state.reflists && state.reflists[subject];

export const selectReflistsPlatform = (state: IRootState): string | undefined =>
  state.reflists && state.reflists.platform;

export const selectReflistsKvk = (state: IRootState): string | undefined =>
  state.reflists && state.reflists.kvk;

export const selectReflistsLang = (state: IRootState): string | undefined =>
  state.reflists && state.reflists.lang;

/* ***** *****  Chapters  ***** ***** */

export const selectChapter = (
  state: IRootState,
  chapterLabel: string,
  subject: EReflistSubject = EReflistSubject.ME
): IReflistChapter => {
  return selectReflists(state, subject)[chapterLabel];
};

export const getNextChapterLabel = (
  state: IRootState,
  chapterLabel: EReflistChapterName
): EReflistChapterName | undefined => {
  if (!chapterExist(state, chapterLabel)) return undefined;

  const chaptersKeys = selectChapterKeys(state);
  const index = selectChapterIndex(state, chapterLabel);
  const next = chaptersKeys[index + 1] as EReflistChapterName;

  return next;
};

export const getPreviousChapterLabel = (
  state: IRootState,
  chapterLabel: EReflistChapterName
): EReflistChapterName | undefined => {
  if (!chapterExist(state, chapterLabel)) return undefined;

  const chaptersKeys = selectChapterKeys(state);
  const index = selectChapterIndex(state, chapterLabel);
  const next = chaptersKeys[index - 1];

  return next;
};

export const selectNumberOfQuestionsForChapter = (
  state: IRootState,
  chapterLabel: EReflistChapterName
): number => {
  const chapter = selectChapter(state, chapterLabel);

  return chapter && chapter.questions && chapter.questions.length;
};

export const hasNextChapter = (
  state: IRootState,
  chapterLabel: EReflistChapterName
): boolean => {
  return !!getNextChapterLabel(state, chapterLabel);
};

export const hasPreviousChapter = (
  state: IRootState,
  chapterLabel: EReflistChapterName
): boolean => {
  return !!getPreviousChapterLabel(state, chapterLabel);
};

/* ***** *****  Questions  ***** ***** */

export const selectQuestions = (
  state: IRootState,
  chapterLabel: EReflistChapterName,
  id: number
): TReflistQuestion[] => {
  const chapter = selectChapter(state, chapterLabel);

  return chapter && chapter.questions && chapter.questions[id];
};

const selectQuestionForReflistKey = (
  state: IRootState,
  reflistKey: string,
  subject: EReflistSubject = EReflistSubject.ME
) => {
  const chapterName = selectChapterNameByReflistKey(state, reflistKey, subject);
  const chapter = selectChapter(state, chapterName as string, subject);
  let question: TReflistQuestion | undefined;

  if (!chapter) return undefined;

  chapter.questions.forEach((values: TReflistQuestion[]) => {
    const questions = values.filter((value: TReflistQuestion) => {
      return value.label === reflistKey;
    });

    if (questions[0]) question = questions[0] as TReflistQuestion;
  });

  return question;
};

export const hasNextQuestions = (
  state: IRootState,
  chapterLabel: EReflistChapterName,
  id: number
): boolean => {
  const nextId = id + 1;
  const question = selectQuestions(state, chapterLabel, nextId);

  return !!question;
};

export const hasPreviousQuestions = (
  state: IRootState,
  chapterLabel: EReflistChapterName,
  id: number
): boolean => {
  const previousId = id - 1;
  const question = selectQuestions(state, chapterLabel, previousId);

  return !!question;
};

export const selectNbQuestionPages = (state: IRootState) => {
  const reflists = selectReflists(state);
  let nbPages = 0;

  Object.values(reflists).forEach(chapter => {
    if (!chapter) return;

    nbPages += chapter.questions.length;
  });

  return nbPages;
};

export const selectNbPreviousQuestionPages = (
  state: IRootState,
  chapterLabel: EReflistChapterName,
  questionIndex: number
) => {
  const chaptersKeys = selectChapterKeys(state);
  const index = selectChapterIndex(state, chapterLabel);

  let nbPages = 0;

  chaptersKeys.slice(0, index).forEach(chapterKey => {
    nbPages += selectNumberOfQuestionsForChapter(state, chapterKey);
  });

  return nbPages + questionIndex;
};

/* ***** *****  Answers  ***** ***** */

const selectAnswersForReflistKey = (
  state: IRootState,
  reflistKey: string,
  subject: EReflistSubject = EReflistSubject.ME
) => {
  const question = selectQuestionForReflistKey(state, reflistKey, subject);
  const answers = question && question.answers;

  return answers;
};

export const selectAnswerLabelById = (
  state: IRootState,
  reflistKey: string,
  answerId: number,
  subject: EReflistSubject = EReflistSubject.ME
) => {
  const answers = selectAnswersForReflistKey(state, reflistKey, subject);
  const answer =
    answers && answers.filter((value: IAnswer) => value.id === answerId);

  return answer && answer[0] && answer[0].label;
};

export const selectProfileJobs = (
  state: IRootState,
  subject: EReflistSubject = EReflistSubject.ME
) => {
  return selectAnswersForReflistKey(state, 'profileJob', subject);
};

export const selectProfileJobQuestionId = (
  state: IRootState,
  subject: EReflistSubject = EReflistSubject.ME
) => {
  return (selectQuestionForReflistKey(
    state,
    'profileJob',
    subject
  ) as TSearchReflistQuestion).questionId;
};

export const selectProfileJobById = (
  state: IRootState,
  answerId: number,
  subject: EReflistSubject = EReflistSubject.OTHER
) => {
  return answerId === 0
    ? ''
    : selectAnswerLabelById(state, 'profileJob', answerId, subject);
};

export const selectQuestionsForKeys = (
  state: IRootState,
  reflistKeys: string[],
  subject: EReflistSubject = EReflistSubject.ME
) => {
  return reflistKeys.map(reflistKey => {
    return selectQuestionForReflistKey(state, reflistKey, subject);
  });
};
